/**
 * API COVERAGE
 *   FilesController   POST /api/files/upload, POST /api/files/upload-multiple,
 *                     GET /api/files, GET /{id}, GET /{id}/download,
 *                     DELETE /{id}, POST /{id}/restore                  → filesApi.*
 *   FoldersController GET /api/folders, POST, PUT /{id}, DELETE /{id}   → foldersApi.*
 */
import { api, http, request } from '@/lib/apiClient';
import type { PagedResult } from '@/types/api';
import type { FileFolder, FileItem, GetFilesParams, UpsertFolderRequest } from '@/types/files';

export const filesApi = {
  list: (params: GetFilesParams) =>
    api.get<PagedResult<FileItem>>('/files', params as Record<string, unknown>),
  get: (id: number) => api.get<FileItem>(`/files/${id}`),
  upload: (
    file: File,
    opts?: { folderId?: number; isPublic?: boolean; entityType?: string; entityId?: number },
  ) => {
    const form = new FormData();
    form.append('File', file);
    if (opts?.folderId != null) form.append('FolderId', String(opts.folderId));
    if (opts?.entityType) form.append('EntityType', opts.entityType);
    if (opts?.entityId != null) form.append('EntityId', String(opts.entityId));
    form.append('IsPublic', String(opts?.isPublic ?? false));
    return request<FileItem>({
      method: 'POST',
      url: '/files/upload',
      data: form,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadMultiple: (files: File[], opts?: { folderId?: number; isPublic?: boolean }) => {
    const form = new FormData();
    files.forEach((f) => form.append('Files', f));
    if (opts?.folderId != null) form.append('FolderId', String(opts.folderId));
    form.append('IsPublic', String(opts?.isPublic ?? false));
    return request<Array<{ fileName: string; success: boolean; fileId?: number | null; error?: string | null }>>({
      method: 'POST',
      url: '/files/upload-multiple',
      data: form,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /** Authenticated binary fetch (the download endpoint requires a Bearer token). */
  fetchBlob: async (id: number): Promise<Blob> => {
    const res = await http.get(`/files/${id}/download`, { responseType: 'blob' });
    return res.data as Blob;
  },
  /** Fetch with auth then trigger a browser save. */
  download: async (id: number, fileName: string): Promise<void> => {
    const blob = await http.get(`/files/${id}/download`, { responseType: 'blob' }).then((r) => r.data as Blob);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  remove: (id: number) => api.del<unknown>(`/files/${id}`),
  restore: (id: number) => api.post<unknown>(`/files/${id}/restore`),
};

export const foldersApi = {
  list: () => api.get<FileFolder[]>('/folders'),
  create: (body: UpsertFolderRequest) => api.post<FileFolder>('/folders', body),
  update: (id: number, body: UpsertFolderRequest) => api.put<FileFolder>(`/folders/${id}`, body),
  remove: (id: number) => api.del<unknown>(`/folders/${id}`),
};
